import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSlug from 'rehype-slug';

/**
 * BlogContent — single renderer used by every blog post on the site.
 *
 * Handles three legacy formats interchangeably:
 *   1. Pure Markdown  (## headings, | tables |, **bold**, etc.)
 *   2. Pure HTML      (<h2>, <table>, <strong>) — the format used by the
 *      earlier hardcoded seed posts
 *   3. Mixed Markdown + inline HTML
 *
 * Achieved via:
 *   - remark-gfm   → GitHub-flavored markdown (tables, strikethrough, task lists)
 *   - rehype-raw   → allows raw HTML inside markdown to pass through unsanitized
 *
 * Design spec (May 2026 — applied via custom component renderers, not prose
 * defaults, so designers can iterate without fighting @tailwindcss/typography):
 *   - Body 18px / line-height 1.75
 *   - H2 26px bold, 40px top margin
 *   - H3 20px semi-bold, 24px top margin
 *   - Blockquote: teal #059669 left border, light grey bg, 16px padding, 4px radius
 *   - Tables: full width, header dark/white, body rows zebra-striped #f9f9f9, 1px #e5e5e5 borders
 *   - Bullet lists: 24px left padding, 8px gap between items
 *   - "Common mistake" paragraphs auto-wrapped in a yellow callout box with orange left border
 *
 * NOTE on safety: blog content is admin-authored only (no user submissions).
 * Allowing raw HTML via rehype-raw is intentional and bounded to that trust
 * boundary. If you later open authorship to untrusted users, add
 * rehype-sanitize to the pipeline.
 */

// ---------- helpers ---------------------------------------------------------

/** Read the visible text content of any ReactMarkdown children tree. */
const textOf = (children) => {
  if (children == null) return '';
  if (typeof children === 'string' || typeof children === 'number') return String(children);
  if (Array.isArray(children)) return children.map(textOf).join('');
  if (children?.props?.children !== undefined) return textOf(children.props.children);
  return '';
};

// ---------- custom block renderers ------------------------------------------

const renderers = {
  // 18px / 1.75 body text
  p: ({ node, children, ...props }) => {
    const text = textOf(children);
    // Spec item 8: "Common mistake" paragraphs become a yellow callout
    if (/^\s*Common mistake\b/i.test(text)) {
      return (
        <div
          className="my-6 rounded-md bg-yellow-50 border-l-4 border-orange-400 p-4 text-[17px] leading-7 text-slate-800"
          data-callout="common-mistake"
        >
          <p {...props} className="m-0">{children}</p>
        </div>
      );
    }
    return (
      <p {...props} className="my-5 text-[18px] leading-[1.75] text-slate-700">
        {children}
      </p>
    );
  },

  // 26px bold, 40px top margin
  h2: ({ node, children, ...props }) => (
    <h2
      {...props}
      className="text-[26px] font-bold text-slate-900 mt-10 mb-4 leading-tight tracking-tight"
    >
      {children}
    </h2>
  ),

  // 20px semi-bold, 24px top margin
  h3: ({ node, children, ...props }) => (
    <h3
      {...props}
      className="text-[20px] font-semibold text-slate-900 mt-6 mb-3 leading-snug"
    >
      {children}
    </h3>
  ),

  h1: ({ node, children, ...props }) => (
    <h1
      {...props}
      className="text-[32px] font-bold text-slate-900 mt-8 mb-5 leading-tight"
    >
      {children}
    </h1>
  ),

  // Blockquote: teal #059669 left border, light grey bg, 16px padding, 4px radius
  blockquote: ({ node, children, ...props }) => (
    <blockquote
      {...props}
      className="my-6 rounded p-4 bg-slate-50 text-slate-800 text-[18px] leading-[1.75] not-italic [&_p]:m-0 [&_p+p]:mt-3"
      style={{ borderLeft: '4px solid #059669' }}
    >
      {children}
    </blockquote>
  ),

  // Bullet lists: 24px left padding, 8px gap between items
  ul: ({ node, children, ordered, ...props }) => (
    <ul
      {...props}
      className="my-5 list-disc text-slate-700 text-[18px] leading-[1.75] space-y-2"
      style={{ paddingLeft: '24px' }}
    >
      {children}
    </ul>
  ),
  ol: ({ node, children, ordered, ...props }) => (
    <ol
      {...props}
      className="my-5 list-decimal text-slate-700 text-[18px] leading-[1.75] space-y-2"
      style={{ paddingLeft: '24px' }}
    >
      {children}
    </ol>
  ),
  li: ({ node, children, ordered, checked, ...props }) => (
    <li {...props} className="pl-1">
      {children}
    </li>
  ),

  // Tables: full width, alternating rows, dark header
  table: ({ node, children, ...props }) => (
    <div className="my-6 overflow-x-auto">
      <table
        {...props}
        className="w-full text-[16px] text-left border-collapse"
        style={{ border: '1px solid #e5e5e5' }}
      >
        {children}
      </table>
    </div>
  ),
  thead: ({ node, children, ...props }) => (
    <thead {...props} className="bg-slate-900 text-white">
      {children}
    </thead>
  ),
  tbody: ({ node, children, ...props }) => (
    <tbody
      {...props}
      className="[&_tr:nth-child(even)]:bg-[#f9f9f9] [&_tr]:bg-white"
    >
      {children}
    </tbody>
  ),
  th: ({ node, children, ...props }) => (
    <th
      {...props}
      className="px-4 py-3 font-semibold text-left"
      style={{ border: '1px solid #e5e5e5' }}
    >
      {children}
    </th>
  ),
  td: ({ node, children, ...props }) => (
    <td
      {...props}
      className="px-4 py-3 align-top"
      style={{ border: '1px solid #e5e5e5', color: '#334155' }}
    >
      {children}
    </td>
  ),

  // Inline elements
  a: ({ node, children, ...props }) => (
    <a {...props} className="text-emerald-600 underline-offset-2 hover:underline">
      {children}
    </a>
  ),
  strong: ({ node, children, ...props }) => (
    <strong {...props} className="font-semibold text-slate-900">
      {children}
    </strong>
  ),
  code: ({ node, inline, children, ...props }) => (
    <code
      {...props}
      className="bg-slate-100 text-slate-900 px-1.5 py-0.5 rounded text-[0.95em]"
    >
      {children}
    </code>
  ),
  hr: ({ node, ...props }) => <hr {...props} className="my-10 border-slate-200" />,
};

const BlogContent = ({ content, className = '', testId = 'blog-content' }) => {
  if (!content) return null;
  return (
    <div
      className={'blog-content font-sans text-slate-700 ' + className}
      data-testid={testId}
      style={{ fontFamily: "Inter, system-ui, -apple-system, 'Segoe UI', sans-serif" }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw, rehypeSlug]}
        components={renderers}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

export default BlogContent;
